/**
 * little PEBBLE interactions (Alpine + scroll reveal)
 */

document.addEventListener('alpine:init', () => {
  Alpine.data('pbHeader', () => ({
    open: false,
    searchOpen: false,
    searchPanelOpen: false,
    syncBody() {
      const locked = this.open || this.searchOpen;
      document.documentElement.classList.toggle('pb-drawer-open', locked);
      if (locked) {
        document.body.style.overflow = 'hidden';
      } else {
        document.body.style.removeProperty('overflow');
      }
    },
    closeAll() {
      this.open = false;
      this.searchOpen = false;
      this.searchPanelOpen = false;
      this.syncBody();
    },
    openSearchPanel() {
      this.searchPanelOpen = true;
      this.open = false;
      this.searchOpen = false;
      this.syncBody();
    },
    closeSearchPanel() {
      this.searchPanelOpen = false;
    },
    maybeCloseSearchPanel(event) {
      const wrap = event.currentTarget;
      requestAnimationFrame(() => {
        if (!wrap.contains(document.activeElement)) {
          this.searchPanelOpen = false;
        }
      });
    },
    toggle() {
      this.open = !this.open;
      if (this.open) {
        this.searchOpen = false;
        this.searchPanelOpen = false;
      }
      this.syncBody();
    },
    toggleSearch() {
      this.searchOpen = !this.searchOpen;
      if (this.searchOpen) {
        this.open = true;
        this.searchPanelOpen = false;
      }
      this.syncBody();
      if (this.searchOpen || this.open) {
        this.$nextTick(() => {
          const input = this.$refs.mobileSearchInput;
          if (input) input.focus();
        });
      }
    },
  }));

  Alpine.data('pbCategories', () => ({
    gender: 'girls',
    progress: 28,
    init() {
      this.$nextTick(() => this.bindTrack());
    },
    bindTrack() {
      const track = this.$refs.track;
      if (!track) return;
      const update = () => {
        const max = track.scrollWidth - track.clientWidth;
        this.progress = max > 0 ? Math.max(18, Math.min(100, (track.scrollLeft / max) * 100)) : 28;
      };
      track.addEventListener('scroll', update, { passive: true });
      update();
    },
    setGender(value) {
      this.gender = value;
      this.$nextTick(() => {
        const track = this.$refs.track;
        if (track) track.scrollTo({ left: 0, behavior: 'smooth' });
        this.bindTrack();
      });
    },
    matches(gender) {
      return gender === this.gender || gender === 'all';
    },
    scrollBy(dir) {
      const track = this.$refs.track;
      if (!track) return;
      const card = track.querySelector('.pb-cat-card');
      const step = card ? card.getBoundingClientRect().width + 24 : 280;
      track.scrollBy({ left: dir * step, behavior: 'smooth' });
    },
  }));

  Alpine.data('pbWords', (initial = 0) => ({
    active: initial,
    setActive(i) {
      this.active = i;
    },
  }));

  Alpine.data('pbProducts', () => ({
    tab: 'trending',
    setTab(value) {
      this.tab = value;
      this.$nextTick(() => {
        const track = this.activeTrack();
        if (track) track.scrollTo({ left: 0, behavior: 'smooth' });
      });
    },
    activeTrack() {
      return this.$el.querySelector(`[data-pb-prod-track="${this.tab}"]`);
    },
    scrollBy(dir) {
      const track = this.activeTrack();
      if (!track) return;
      const card = track.querySelector('.pb-pcard');
      const styles = window.getComputedStyle(track);
      const gap = parseFloat(styles.columnGap || styles.gap) || 16;
      const step = card ? card.getBoundingClientRect().width + gap : track.clientWidth * 0.75;
      track.scrollBy({ left: dir * step, behavior: 'smooth' });
    },
  }));

  Alpine.data('pbOutfit', () => ({
    step: 0,
    selected: {},
    setStep(i) {
      this.step = i;
    },
    toggle(id) {
      this.selected[id] = !this.selected[id];
    },
    isSelected(id) {
      return Boolean(this.selected[id]);
    },
  }));

  const pbScrollingCardsFactory = () => ({
    // kept for any leftover markup; real motion is vanilla initPbScrollingCards()
    init() {},
  });

  Alpine.data('pbScrollingCards', pbScrollingCardsFactory);
  Alpine.data('pbFeature', pbScrollingCardsFactory);
});

/**
 * NEW IN heading stack — vanilla scroll progress (no Alpine dependency).
 * Translates stacked headings while cards scroll past sticky center copy.
 */
function initPbScrollingCards(scope) {
  const rootScope = scope && scope.querySelectorAll ? scope : document;
  const roots = rootScope.querySelectorAll
    ? rootScope.querySelectorAll('[data-pb-scrolling-cards]')
    : [];

  roots.forEach((root) => {
    if (root.dataset.pbScBound === '1') return;
    root.dataset.pbScBound = '1';

    const wrap = root.querySelector('[data-pb-heading-wrap]');
    const track = root.querySelector('[data-pb-headings]');
    if (!wrap || !track) return;

    const desktopMq = window.matchMedia('(min-width: 900px)');
    let translateMax = 0;
    let active = false;
    let looping = false;

    const measure = () => {
      const items = Array.from(track.querySelectorAll('.pb-scrollcards__heading'));
      if (!items.length) return;

      if (!desktopMq.matches) {
        wrap.style.height = '';
        track.style.transform = 'translate3d(0,0,0)';
        items.forEach((el) => {
          el.style.minHeight = '';
        });
        translateMax = 0;
        return;
      }

      const styles = getComputedStyle(items[0]);
      const fontSize = parseFloat(styles.fontSize) || 40;
      let lineHeight = parseFloat(styles.lineHeight);
      if (!lineHeight || Number.isNaN(lineHeight)) lineHeight = fontSize * 1.15;
      const phaseHeight = Math.round(lineHeight * 2.35);

      items.forEach((el) => {
        el.style.minHeight = `${phaseHeight}px`;
      });
      wrap.style.height = `${phaseHeight}px`;
      root.style.setProperty('--pb-sc-heading-h', `${phaseHeight}px`);
      translateMax = -phaseHeight * Math.max(0, items.length - 1);
    };

    const update = () => {
      if (!desktopMq.matches) return;
      if (!translateMax) measure();
      if (!translateMax) return;

      const rect = root.getBoundingClientRect();
      const view = window.innerHeight || 1;
      const travel = Math.max(1, root.offsetHeight - view);
      const progress = Math.min(1, Math.max(0, -rect.top / travel));
      const y = Math.round(translateMax * progress * 10) / 10;
      track.style.transform = `translate3d(0, ${y}px, 0)`;
    };

    const loop = () => {
      if (!active) {
        looping = false;
        return;
      }
      update();
      requestAnimationFrame(loop);
    };

    const startLoop = () => {
      active = true;
      if (!looping) {
        looping = true;
        requestAnimationFrame(loop);
      }
    };

    measure();
    update();

    window.addEventListener('resize', () => {
      measure();
      update();
    }, { passive: true });

    const io = new IntersectionObserver(
      (entries) => {
        const visible = entries.some((e) => e.isIntersecting);
        if (visible) startLoop();
        else active = false;
      },
      { root: null, threshold: 0, rootMargin: '100px 0px' }
    );
    io.observe(root);

    // kick once in case already on screen
    const rect = root.getBoundingClientRect();
    if (rect.bottom > 0 && rect.top < window.innerHeight) startLoop();

    requestAnimationFrame(() => {
      measure();
      update();
    });
    setTimeout(() => {
      measure();
      update();
    }, 300);
  });
}

window.initPbScrollingCards = initPbScrollingCards;
function initPebbleReveal() {
  const nodes = document.querySelectorAll('.pb-reveal:not([data-bound]), .pebble-reveal:not([data-bound])');
  if (!nodes.length) return;

  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
    nodes.forEach((el) => {
      el.classList.add('is-visible');
      el.setAttribute('data-bound', '1');
    });
    return;
  }

  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('is-visible');
        observer.unobserve(entry.target);
      });
    },
    { threshold: 0.12, rootMargin: '0px 0px -40px 0px' }
  );

  nodes.forEach((el) => {
    el.setAttribute('data-bound', '1');
    observer.observe(el);
  });
}

async function pebbleAddToCart(items) {
  const payload = Array.isArray(items) ? items : [items];
  const root = window.Shopify?.routes?.root || '/';
  const response = await fetch(`${root}cart/add.js`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({ items: payload }),
  });
  if (!response.ok) throw new Error('Unable to add to cart');

  const result = await response.json();
  const sectionResponse = await fetch(`${root}?sections=cart-drawer-section`, {
    headers: { Accept: 'application/json' },
  });

  if (sectionResponse.ok) {
    const sections = await sectionResponse.json();
    const html = sections['cart-drawer-section'];
    const currentSection = document.getElementById('shopify-section-cart-drawer-section');

    if (html && currentSection) {
      const template = document.createElement('template');
      template.innerHTML = html.trim();
      const updatedSection = template.content.querySelector('#shopify-section-cart-drawer-section');
      if (updatedSection) currentSection.replaceWith(updatedSection);
    }
  }

  const drawer = document.querySelector('theme-drawer#cart-drawer');
  if (drawer?.open) drawer.open();
  return result;
}

window.pebbleAddToCart = pebbleAddToCart;

/**
 * Layered sticky cards — scale previous cards as the next one covers them.
 */
function initPbLayeredCards(scope) {
  const rootScope = scope && scope.querySelectorAll ? scope : document;
  const roots = rootScope.querySelectorAll
    ? rootScope.querySelectorAll('[data-pb-layered-cards]')
    : [];

  roots.forEach((root) => {
    if (root.dataset.pbLayerBound === '1') return;
    root.dataset.pbLayerBound = '1';

    const cards = Array.from(root.querySelectorAll('.pb-layered__card'));
    if (cards.length < 2) return;

    const desktopMq = window.matchMedia('(min-width: 900px)');
    let active = false;
    let looping = false;

    const update = () => {
      if (!desktopMq.matches) {
        cards.forEach((card) => {
          const inner = card.querySelector('.pb-layered__card-inner');
          if (inner) inner.style.transform = '';
        });
        return;
      }

      const viewTop =
        parseFloat(
          getComputedStyle(document.documentElement).getPropertyValue('--header-group-height')
        ) || 96;

      cards.forEach((card, index) => {
        const inner = card.querySelector('.pb-layered__card-inner');
        if (!inner) return;

        if (index === cards.length - 1) {
          inner.style.transform = 'scale3d(1, 1, 1)';
          return;
        }

        const next = cards[index + 1];
        const cardRect = card.getBoundingClientRect();
        const nextRect = next.getBoundingClientRect();
        const stickyY = viewTop + 24;
        // How far the next (front) card has climbed over this one
        const overlap = stickyY + cardRect.height - nextRect.top;
        const progress = Math.min(1, Math.max(0, overlap / Math.max(1, cardRect.height * 0.75)));
        // Back card shrinks more (~18%) so front stays clearly larger
        const scale = 1 - progress * 0.18;
        inner.style.transform = `scale3d(${scale}, ${scale}, 1)`;
      });
    };

    const loop = () => {
      if (!active) {
        looping = false;
        return;
      }
      update();
      requestAnimationFrame(loop);
    };

    const startLoop = () => {
      active = true;
      if (!looping) {
        looping = true;
        requestAnimationFrame(loop);
      }
    };

    window.addEventListener('resize', update, { passive: true });

    const io = new IntersectionObserver(
      (entries) => {
        const visible = entries.some((e) => e.isIntersecting);
        if (visible) startLoop();
        else active = false;
      },
      { root: null, threshold: 0, rootMargin: '80px 0px' }
    );
    io.observe(root);

    const rect = root.getBoundingClientRect();
    if (rect.bottom > 0 && rect.top < window.innerHeight) startLoop();
    update();
  });
}

window.initPbLayeredCards = initPbLayeredCards;

/**
 * Highlight lookbook slider — drag-to-scroll + staggered cards
 */
function initPbHighlightSlider(scope) {
  const rootScope = scope && scope.querySelectorAll ? scope : document;
  const sliders = rootScope.querySelectorAll
    ? rootScope.querySelectorAll('[data-pb-ht-slider]')
    : [];

  sliders.forEach((slider) => {
    if (slider.dataset.pbHtBound === '1') return;
    slider.dataset.pbHtBound = '1';

    const track = slider.querySelector('[data-pb-ht-track]');
    const prev = slider.querySelector('[data-pb-ht-prev]');
    const next = slider.querySelector('[data-pb-ht-next]');
    if (!track) return;

    const step = () => {
      const card = track.querySelector('.pb-ht-card');
      return card ? card.getBoundingClientRect().width + 18 : 260;
    };

    if (prev) {
      prev.addEventListener('click', () => {
        track.scrollBy({ left: -step(), behavior: 'smooth' });
      });
    }
    if (next) {
      next.addEventListener('click', () => {
        track.scrollBy({ left: step(), behavior: 'smooth' });
      });
    }

    let isDown = false;
    let startX = 0;
    let scrollLeft = 0;
    let moved = false;

    const onDown = (e) => {
      isDown = true;
      moved = false;
      track.classList.add('is-dragging');
      startX = (e.pageX || e.touches?.[0]?.pageX || 0) - track.offsetLeft;
      scrollLeft = track.scrollLeft;
    };

    const onUp = () => {
      isDown = false;
      track.classList.remove('is-dragging');
    };

    const onMove = (e) => {
      if (!isDown) return;
      const x = (e.pageX || e.touches?.[0]?.pageX || 0) - track.offsetLeft;
      const walk = (x - startX) * 1.15;
      if (Math.abs(walk) > 4) moved = true;
      track.scrollLeft = scrollLeft - walk;
      if (e.cancelable) e.preventDefault();
    };

    track.addEventListener('mousedown', onDown);
    window.addEventListener('mouseup', onUp);
    window.addEventListener('mousemove', onMove);
    track.addEventListener('touchstart', onDown, { passive: true });
    track.addEventListener('touchend', onUp);
    track.addEventListener('touchmove', onMove, { passive: false });

    track.addEventListener('click', (e) => {
      if (moved) {
        e.preventDefault();
        e.stopPropagation();
      }
    }, true);
  });
}

window.initPbHighlightSlider = initPbHighlightSlider;

/**
 * Products highlight — one-at-a-time product carousel + video controls
 */
function initPbProductsHighlight(scope) {
  const rootScope = scope && scope.querySelectorAll ? scope : document;
  const roots = rootScope.querySelectorAll
    ? rootScope.querySelectorAll('[data-pb-products-highlight]')
    : [];

  roots.forEach((root) => {
    if (root.dataset.pbPhBound === '1') return;
    root.dataset.pbPhBound = '1';

    const carousel = root.querySelector('[data-pb-ph-carousel]');
    if (carousel) {
      const track = carousel.querySelector('[data-pb-ph-track]');
      const slides = Array.from(carousel.querySelectorAll('[data-pb-ph-slide]'));
      const counter = carousel.querySelector('[data-pb-ph-counter]');
      const progress = carousel.querySelector('[data-pb-ph-progress]');
      const prev = carousel.querySelector('[data-pb-ph-prev]');
      const next = carousel.querySelector('[data-pb-ph-next]');
      let index = 0;

      const render = () => {
        const total = Math.max(slides.length, 1);
        if (track) {
          track.style.transform = `translateX(-${index * 100}%)`;
        }
        if (counter) counter.textContent = `${index + 1} / ${total}`;
        if (progress) {
          const ratio = (index + 1) / total;
          progress.style.transform = `scaleX(${ratio})`;
        }
        if (prev) prev.disabled = index <= 0;
        if (next) next.disabled = index >= slides.length - 1;
      };

      if (track) {
        track.style.transition = 'transform 0.35s cubic-bezier(0.16, 1, 0.3, 1)';
        track.style.willChange = 'transform';
      }
      if (progress) {
        progress.style.transformOrigin = 'left center';
      }

      prev?.addEventListener('click', () => {
        if (index > 0) {
          index -= 1;
          render();
        }
      });
      next?.addEventListener('click', () => {
        if (index < slides.length - 1) {
          index += 1;
          render();
        }
      });

      if (track && slides.length > 1) {
        let dragging = false;
        let startX = 0;
        let deltaX = 0;
        let width = track.getBoundingClientRect().width || 1;

        const onDown = (e) => {
          dragging = true;
          startX = e.clientX;
          deltaX = 0;
          width = track.getBoundingClientRect().width || 1;
          track.style.transition = 'none';
          track.setPointerCapture?.(e.pointerId);
        };
        const onMove = (e) => {
          if (!dragging) return;
          deltaX = e.clientX - startX;
          const atStart = index === 0 && deltaX > 0;
          const atEnd = index === slides.length - 1 && deltaX < 0;
          const resisted = atStart || atEnd ? deltaX * 0.35 : deltaX;
          const percent = (resisted / width) * 100;
          track.style.transform = `translateX(calc(-${index * 100}% + ${percent}%))`;
        };
        const onUp = () => {
          if (!dragging) return;
          dragging = false;
          track.style.transition = 'transform 0.35s cubic-bezier(0.16, 1, 0.3, 1)';
          const threshold = width * 0.18;
          if (deltaX <= -threshold && index < slides.length - 1) {
            index += 1;
          } else if (deltaX >= threshold && index > 0) {
            index -= 1;
          }
          deltaX = 0;
          render();
        };

        track.addEventListener('pointerdown', onDown);
        track.addEventListener('pointermove', onMove);
        track.addEventListener('pointerup', onUp);
        track.addEventListener('pointercancel', onUp);
        track.addEventListener('pointerleave', () => { if (dragging) onUp(); });
        track.addEventListener('dragstart', (e) => e.preventDefault());
      }

      render();
    }

    const videoWrap = root.querySelector('[data-pb-ph-video]');
    const video = videoWrap?.querySelector('video');
    if (videoWrap && video) {
      const muteBtn = videoWrap.querySelector('[data-pb-ph-mute]');
      const playBtn = videoWrap.querySelector('[data-pb-ph-play]');
      const muteIcon = muteBtn?.querySelector('.pb-ph__icon-mute');
      const unmuteIcon = muteBtn?.querySelector('.pb-ph__icon-unmute');
      const playIcon = playBtn?.querySelector('.pb-ph__icon-play');
      const pauseIcon = playBtn?.querySelector('.pb-ph__icon-pause');

      const syncMute = () => {
        if (muteIcon) muteIcon.hidden = !video.muted;
        if (unmuteIcon) unmuteIcon.hidden = video.muted;
        if (muteBtn) muteBtn.setAttribute('aria-label', video.muted ? 'Unmute' : 'Mute');
      };
      const syncPlay = () => {
        const paused = video.paused;
        if (playIcon) playIcon.hidden = !paused;
        if (pauseIcon) pauseIcon.hidden = paused;
        if (playBtn) playBtn.setAttribute('aria-label', paused ? 'Play video' : 'Pause video');
      };

      muteBtn?.addEventListener('click', () => {
        video.muted = !video.muted;
        syncMute();
      });
      playBtn?.addEventListener('click', () => {
        if (video.paused) video.play();
        else video.pause();
        syncPlay();
      });
      video.addEventListener('play', syncPlay);
      video.addEventListener('pause', syncPlay);
      syncMute();
      syncPlay();
    }
  });
}

window.initPbProductsHighlight = initPbProductsHighlight;

function initPbFlipTop(scope) {
  const rootScope = scope && scope.querySelectorAll ? scope : document;
  const roots = rootScope.querySelectorAll ? rootScope.querySelectorAll('[data-pb-ft]') : [];

  roots.forEach((root) => {
    if (root.dataset.pbFtBound === '1') return;
    root.dataset.pbFtBound = '1';

    const video = root.querySelector('[data-pb-ft-video] video') || root.querySelector('.pb-ft__media video');
    const pauseBtn = root.querySelector('[data-pb-ft-pause]');
    const pauseLabel = root.querySelector('[data-pb-ft-pause-label]');
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (video) {
      if (reduceMotion) {
        video.pause();
        video.removeAttribute('autoplay');
        if (pauseLabel) pauseLabel.textContent = 'Play';
        if (pauseBtn) pauseBtn.setAttribute('aria-label', 'Play video');
      }
      pauseBtn?.addEventListener('click', () => {
        if (video.paused) {
          video.play();
          if (pauseLabel) pauseLabel.textContent = 'Pause';
          pauseBtn.setAttribute('aria-label', 'Pause video');
        } else {
          video.pause();
          if (pauseLabel) pauseLabel.textContent = 'Play';
          pauseBtn.setAttribute('aria-label', 'Play video');
        }
      });
    }

    const track = root.querySelector('[data-pb-ft-track]');
    const prev = root.querySelector('[data-pb-ft-prev]');
    const next = root.querySelector('[data-pb-ft-next]');
    const step = () => {
      if (!track) return 0;
      const slide = track.querySelector('.pb-ft__slide');
      const styles = window.getComputedStyle(track);
      const gap = parseFloat(styles.columnGap || styles.gap) || 16;
      return slide ? slide.getBoundingClientRect().width + gap : track.clientWidth * 0.8;
    };
    prev?.addEventListener('click', () => track?.scrollBy({ left: -step(), behavior: 'smooth' }));
    next?.addEventListener('click', () => track?.scrollBy({ left: step(), behavior: 'smooth' }));
  });
}
window.initPbFlipTop = initPbFlipTop;

/**
 * Style It accordion — one step open at a time
 */
function initPbStyleIt(scope) {
  const rootScope = scope && scope.querySelectorAll ? scope : document;
  const roots = rootScope.querySelectorAll
    ? rootScope.querySelectorAll('[data-pb-styleit]')
    : [];

  roots.forEach((root) => {
    if (root.dataset.pbStyleBound === '1') return;
    root.dataset.pbStyleBound = '1';

    const panels = Array.from(root.querySelectorAll('[data-pb-styleit-panel]'));
    if (!panels.length) return;

    const setOpen = (target) => {
      panels.forEach((panel) => {
        const trigger = panel.querySelector('[data-pb-styleit-trigger]');
        const body = panel.querySelector('[data-pb-styleit-body]');
        const open = panel === target;
        panel.classList.toggle('is-open', open);
        if (trigger) trigger.setAttribute('aria-expanded', open ? 'true' : 'false');
        if (body) {
          if (open) body.removeAttribute('hidden');
          else body.setAttribute('hidden', '');
        }
      });
    };

    panels.forEach((panel) => {
      const trigger = panel.querySelector('[data-pb-styleit-trigger]');
      trigger?.addEventListener('click', () => {
        if (panel.classList.contains('is-open')) return;
        setOpen(panel);
      });
    });
  });
}

window.initPbStyleIt = initPbStyleIt;

const PB_COLOR_HEX = {
  black: '#1c1c1c',
  navy: '#0B2E59',
  blue: '#2381C4',
  charcoal: '#454545',
  white: '#f3f3f3',
  blush: '#e8b4b8',
  gray: '#8a8a8a',
  grey: '#8a8a8a',
  pink: '#e57aa4',
  silver: '#c5c5c5',
  gold: '#EAAA1E',
  rose: '#d4a0a8',
  lilac: '#c4b0d8',
  mermaid: '#3d6b7a',
  glitter: '#b8bcc4',
  gum: '#c9a07a',
  leather: '#2a2a2a',
  jersey: '#8b8f96',
};

function pbColorHex(label) {
  if (!label) return '#cfd5dc';
  const raw = String(label).trim();
  if (/^#([0-9a-f]{3}|[0-9a-f]{6})$/i.test(raw)) return raw;
  const key = raw.toLowerCase().split(/[\s/;,-]+/)[0];
  return PB_COLOR_HEX[key] || '#cfd5dc';
}

function initPbProductCards(scope) {
  const rootScope = scope && scope.querySelectorAll ? scope : document;
  const cards = rootScope.querySelectorAll
    ? rootScope.querySelectorAll('[data-pb-pcard]')
    : [];

  cards.forEach((card) => {
    if (card.dataset.pbPcardBound === '1') return;
    card.dataset.pbPcardBound = '1';
    const jsonEl = card.querySelector('[data-pb-pcard-colors]');
    let colors = [];
    try {
      colors = JSON.parse((jsonEl && jsonEl.textContent) || '[]');
    } catch (e) {
      colors = [];
    }
    if (!Array.isArray(colors) || !colors.length) return;

    const host = card.querySelector('[data-pb-pcard-swatches]');
    const img = card.querySelector('.pb-pcard__img--primary');
    const titleEl = card.querySelector('[data-pb-pcard-title]');
    const priceNow = card.querySelector('[data-pb-pcard-price-now]');
    const priceWrap = card.querySelector('[data-pb-pcard-price]');
    const links = card.querySelectorAll('[data-pb-pcard-link]');
    if (!host) return;

    const hasRealColors = colors.some((c) => c.label && c.label !== c.title);
    if (colors.length < 2 && !hasRealColors) {
      host.replaceChildren();
      host.hidden = true;
      return;
    }

    const apply = (item, btn) => {
      if (!item) return;
      host.querySelectorAll('[data-pb-pcard-swatch]').forEach((b) => {
        const on = b === btn;
        b.classList.toggle('is-active', on);
        b.setAttribute('aria-pressed', on ? 'true' : 'false');
      });
      if (item.image && img) {
        img.src = item.image;
        if (img.hasAttribute('srcset')) img.removeAttribute('srcset');
        img.alt = item.title || img.alt;
      }
      if (item.title && titleEl) titleEl.textContent = item.title;
      if (item.price && priceNow) priceNow.textContent = item.price;
      if (priceWrap && item.compare) {
        let strike = priceWrap.querySelector('s');
        if (!strike) {
          strike = document.createElement('s');
          priceWrap.insertBefore(strike, priceNow);
        }
        strike.textContent = item.compare;
        priceWrap.classList.add('pb-pcard__price--sale');
      } else if (priceWrap) {
        const strike = priceWrap.querySelector('s');
        if (strike) strike.remove();
        priceWrap.classList.remove('pb-pcard__price--sale');
      }
      if (item.url) {
        links.forEach((a) => {
          a.setAttribute('href', item.url);
          if (a.hasAttribute('aria-label')) a.setAttribute('aria-label', item.title || '');
        });
      }
    };

    host.hidden = false;
    host.innerHTML = colors
      .map((c, i) => {
        const hex = c.hex || pbColorHex(c.label);
        const active = c.current || i === 0;
        return `<button type="button" class="pb-pcard__swatch${active ? ' is-active' : ''}" data-pb-pcard-swatch="${i}" aria-pressed="${active ? 'true' : 'false'}" title="${String(c.label || '').replace(/"/g, '&quot;')}" style="background:${hex}"></button>`;
      })
      .join('');

    host.querySelectorAll('[data-pb-pcard-swatch]').forEach((btn) => {
      btn.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        const item = colors[Number(btn.getAttribute('data-pb-pcard-swatch'))];
        apply(item, btn);
      });
    });
  });
}
window.initPbProductCards = initPbProductCards;

/**
 * Find Your Fit — Brand → Style → Size → Matches guided shopping
 */
function initPbFindYourFit(scope) {
  const rootScope = scope && scope.querySelectorAll ? scope : document;
  const roots = rootScope.querySelectorAll
    ? rootScope.querySelectorAll('[data-pb-fit]')
    : [];

  const CHECK_SVG =
    '<svg class="pb-svg-icon" width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M5 12.5l4.5 4.5L19 7" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>';
  const BAG_SVG =
    '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M6.5 9.5h11l-.8 9.1a1.6 1.6 0 0 1-1.6 1.4H8.9a1.6 1.6 0 0 1-1.6-1.4L6.5 9.5Z" stroke="currentColor" stroke-width="1.7" stroke-linejoin="round"/><path d="M9 9.5V7.8a3 3 0 0 1 6 0v1.7" stroke="currentColor" stroke-width="1.7" stroke-linecap="round"/></svg>';

  roots.forEach((root) => {
    if (root.dataset.pbFitBound === '1') return;
    root.dataset.pbFitBound = '1';

    const catalogEl = root.querySelector('[data-pb-fit-catalog]');
    let catalog = [];
    try {
      catalog = JSON.parse((catalogEl && catalogEl.textContent) || '[]');
    } catch (e) {
      catalog = [];
    }
    if (!Array.isArray(catalog)) catalog = [];

    let messages = {};
    try {
      messages = JSON.parse((root.querySelector('[data-pb-fit-translations]') || {}).textContent || '{}');
    } catch (e) {
      messages = {};
    }
    const STYLE_LABELS = {
      afo: messages.afo || 'AFO-Friendly',
      adaptive: messages.adaptive || 'Adaptive',
      ortho: messages.orthopedic || 'Orthopedic',
      double_zip: messages.doubleZip || 'Double Zip',
      full_wrap: messages.fullWrapZip || 'Full Wrap Zip',
      short_wrap: messages.shortWrapZip || 'Short Wrap Zip',
      straight_zip: messages.straightZip || 'Straight Zip',
      kids: messages.kids || 'Kids',
      women: messages.women || 'Women',
      men: messages.men || 'Men',
    };

    const resultLimit = Number(root.dataset.resultLimit || 4);
    const shopAll = root.dataset.shopAll || '/collections/all';
    const track = root.querySelector('[data-pb-fit-track]');
    const trackWrap = root.querySelector('.pb-fit__track-wrap');
    const empty = root.querySelector('[data-pb-fit-empty]');
    const sizesHost = root.querySelector('[data-pb-fit-sizes]');
    const viewAll = root.querySelector('[data-pb-fit-viewall]');
    const state = { brand: '', style: '', size: '', activeStep: 'brand' };

    const escapeHtml = (str) =>
      String(str == null ? '' : str)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');

    const isMobileLayout = () => window.matchMedia('(max-width: 1024px)').matches;

    const filterProducts = (opts = {}) => {
      const brand = opts.brand !== undefined ? opts.brand : state.brand;
      const style = opts.style !== undefined ? opts.style : state.style;
      const size = opts.size !== undefined ? opts.size : state.size;
      return catalog.filter((p) => {
        if (brand && p.brand !== brand) return false;
        if (style && !(p.styles || []).includes(style)) return false;
        if (size) {
          const sizes = p.sizes || [];
          if (!sizes.length) return false;
          const hit = sizes.find((s) => String(s.label) === String(size));
          if (!hit || hit.available === false) return false;
        }
        return true;
      });
    };

    const pickBadge = (p) => {
      const order = state.style
        ? [state.style, 'afo', 'adaptive', 'ortho', 'full_wrap', 'short_wrap', 'double_zip', 'straight_zip', 'kids', 'women', 'men']
        : ['afo', 'adaptive', 'ortho', 'full_wrap', 'short_wrap', 'double_zip', 'straight_zip', 'kids', 'women', 'men'];
      const styles = p.styles || [];
      for (const key of order) {
        if (styles.includes(key)) {
          return { key, label: STYLE_LABELS[key] || key };
        }
      }
      return null;
    };

    const CARD_HEX = PB_COLOR_HEX;

    const swatchFor = (p) => {
      const label = p.color_label || '';
      const key = String(label).toLowerCase().split(/[\s/;,-]+/)[0];
      return CARD_HEX[key] || '#cfd5dc';
    };

    const colorwaysFor = (p) => {
      if (p.ygroup) {
        const group = catalog.filter((x) => x.ygroup && x.ygroup === p.ygroup);
        if (group.length > 1) return group;
      }
      return [p];
    };

    const isBillyProduct = (p) => {
      const brand = String(p.brand || '').toLowerCase();
      const vendor = String(p.vendor || '').toLowerCase();
      return brand === 'billy' || vendor.includes('billy');
    };

    const ratingFor = (p) => {
      if (!isBillyProduct(p)) return null;
      const rating = Number(p.rating);
      if (!Number.isFinite(rating) || rating <= 0) return null;
      return rating;
    };

    const ratingHtml = (p) => {
      const rating = ratingFor(p);
      if (rating == null) return '';
      const fullStars = Math.floor(rating);
      const stars = Array.from({ length: 5 }, (_, index) => {
        const position = index + 1;
        const stateClass = position <= fullStars ? ' is-full' : position === fullStars + 1 ? ' is-partial' : '';
        return `<span class="pb-fit__rating-star${stateClass}">★</span>`;
      }).join('');
      const ratingLabel = (messages.rated || 'Rated {{ rating }} out of 5').replace('{{ rating }}', rating.toFixed(1));
      return `<div class="pb-fit__rating" aria-label="${escapeHtml(ratingLabel)}">
        <span class="pb-fit__rating-stars" aria-hidden="true">${stars}</span>
        <span class="pb-fit__rating-value">${rating.toFixed(1)}/5</span>
      </div>`;
    };

    const cardHtml = (p) => {
      const badge = pickBadge(p);
      const badgeHtml = badge
        ? `<span class="pb-fit__badge pb-fit__badge--${escapeHtml(badge.key)}">${escapeHtml(badge.label)}</span>`
        : '';
      const compare = p.compare_formatted
        ? `<s>${escapeHtml(p.compare_formatted)}</s>`
        : '';
      const img = p.image
        ? `<img data-pb-fit-img src="${escapeHtml(p.image)}" alt="${escapeHtml(p.title)}" loading="lazy" width="400" height="400">`
        : '';
      const vendor = (p.vendor || '').toUpperCase();
      const ways = colorwaysFor(p);
      const swatches = ways
        .map((c) => {
          const on = c.id === p.id || c.handle === p.handle;
          return `<button type="button" class="pb-pcard__swatch${on ? ' is-active' : ''}" data-pb-fit-colorway="${escapeHtml(c.handle || '')}" title="${escapeHtml(c.color_label || c.title || '')}" style="background:${swatchFor(c)}" aria-pressed="${on ? 'true' : 'false'}"></button>`;
        })
        .join('');
      return `<article class="pb-fit__card" data-pb-fit-card>
        <a class="pb-fit__card-media" data-pb-fit-link href="${escapeHtml(p.url)}" aria-label="${escapeHtml(p.title)}">
          ${badgeHtml}
          ${img}
        </a>
        <div class="pb-fit__card-body">
          ${vendor ? `<p class="pb-fit__card-vendor">${escapeHtml(vendor)}</p>` : ''}
          <h4 class="pb-fit__card-title"><a data-pb-fit-link data-pb-fit-title href="${escapeHtml(p.url)}">${escapeHtml(p.title)}</a></h4>
          ${ratingHtml(p)}
          <p class="pb-fit__card-price">${compare}<span data-pb-fit-price>${escapeHtml(p.price_formatted || '')}</span></p>
          ${swatches ? `<div class="pb-pcard__swatches" aria-label="${escapeHtml(messages.colors || 'Colors')}">${swatches}</div>` : ''}
          <a class="pb-fit__cta" data-pb-fit-link href="${escapeHtml(p.url)}">${BAG_SVG}<span>${escapeHtml(messages.viewStyle || 'View style')}</span></a>
        </div>
      </article>`;
    };

    const setProgress = () => {
      const hasBrand = !!state.brand;
      const hasStyle = !!state.style;
      const hasSize = !!state.size;
      const hasFilter = hasBrand || hasStyle || hasSize;
      const focus = state.activeStep || 'brand';

      root.querySelectorAll('[data-pb-fit-prog]').forEach((item) => {
        const key = item.getAttribute('data-pb-fit-prog');
        const done =
          (key === 'brand' && hasBrand) ||
          (key === 'style' && hasStyle) ||
          (key === 'size' && hasSize) ||
          (key === 'matches' && hasFilter);
        item.classList.toggle('is-done', done);
        item.classList.toggle('is-active', key === focus);
      });

      root.querySelectorAll('[data-pb-fit-rail]').forEach((rail) => {
        const key = rail.getAttribute('data-pb-fit-rail');
        const lit =
          (key === 'style' && hasBrand) ||
          (key === 'size' && hasStyle) ||
          (key === 'matches' && hasSize);
        rail.classList.toggle('is-done', lit);
      });
    };

    const openStep = (key) => {
      if (key === 'matches') {
        state.activeStep = 'matches';
        const results = root.querySelector('[data-pb-fit-results]');
        results?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        setProgress();
        return;
      }
      state.activeStep = key;
      root.querySelectorAll('[data-pb-fit-step]').forEach((step) => {
        const active = step.getAttribute('data-pb-fit-step') === key;
        const body = step.querySelector('[data-pb-fit-body]');
        const toggle = step.querySelector('[data-pb-fit-toggle]');
        step.classList.toggle('is-active', active);
        if (body) {
          // Desktop: keep all open via CSS; still manage hidden for mobile/tablet
          if (!isMobileLayout()) {
            body.removeAttribute('hidden');
          } else if (active) {
            body.removeAttribute('hidden');
          } else {
            body.setAttribute('hidden', '');
          }
        }
        if (toggle) toggle.setAttribute('aria-expanded', active ? 'true' : 'false');
      });
      setProgress();
    };

    const syncDesktopBodies = () => {
      if (isMobileLayout()) return;
      root.querySelectorAll('[data-pb-fit-body]').forEach((body) => {
        body.removeAttribute('hidden');
      });
    };

    const updateStyleAvailability = () => {
      const base = filterProducts({ style: '', size: '' });
      root.querySelectorAll('[data-pb-fit-style]').forEach((btn) => {
        const style = btn.getAttribute('data-pb-fit-style');
        const available = base.some((p) => (p.styles || []).includes(style));
        btn.disabled = !available && state.style !== style;
        btn.classList.toggle('is-disabled', btn.disabled);
      });
    };

    const renderSizes = () => {
      if (!sizesHost) return;
      const pool = filterProducts({ size: '' });
      const sizeMap = new Map();
      pool.forEach((p) => {
        (p.sizes || []).forEach((s) => {
          if (!s || s.label == null || s.label === '') return;
          const key = String(s.label);
          const prev = sizeMap.get(key) || { label: key, available: false };
          if (s.available !== false) prev.available = true;
          sizeMap.set(key, prev);
        });
      });
      const sizes = Array.from(sizeMap.values()).sort((a, b) =>
        String(a.label).localeCompare(String(b.label), undefined, { numeric: true })
      );

      if (!state.brand && !state.style) {
        sizesHost.innerHTML = `<p class="pb-fit__size-empty">${escapeHtml(messages.sizeEmpty || 'Choose a brand or style to see available sizes')}</p>`;
        return;
      }
      if (!sizes.length) {
        sizesHost.innerHTML = `<p class="pb-fit__size-empty">${escapeHtml(messages.noSizes || 'No sized variants for this selection')}</p>`;
        return;
      }

      sizesHost.innerHTML = sizes
        .map((s) => {
          const active = state.size === s.label;
          const disabled = !s.available;
          return `<button type="button" class="pb-fit__chip pb-fit__chip--size${active ? ' is-active' : ''}" data-pb-fit-size="${escapeHtml(s.label)}" aria-pressed="${active ? 'true' : 'false'}" ${disabled ? 'disabled' : ''}><span class="pb-fit__chip-label">${escapeHtml(s.label)}</span><span class="pb-fit__chip-check" aria-hidden="true">${CHECK_SVG}</span></button>`;
        })
        .join('');
    };

    const buildViewAllHref = () => {
      if (state.brand === 'billy') return root.dataset.urlBilly || '/collections/billy-footwear';
      if (state.brand === 'princepard') return root.dataset.urlPrincepard || '/collections/princepard';
      if (state.style === 'afo') return root.dataset.urlAfo || '/collections/afo-friendly';
      if (state.style === 'adaptive') return root.dataset.urlBilly || '/collections/billy-footwear';
      if (state.style === 'kids') return root.dataset.urlKids || '/collections/kids';
      if (state.style === 'ortho') return root.dataset.urlOrtho || '/collections/orthopaedic-support';
      if (state.style === 'women') return root.dataset.urlWomen || '/collections/women-footwear';
      if (state.style === 'men') return root.dataset.urlMen || '/collections/men-footwear';
      if (state.style === 'full_wrap') return root.dataset.urlFullWrap || '/collections/billy-kids-full-wrap-zip';
      if (state.style === 'short_wrap') return root.dataset.urlShortWrap || '/collections/billy-kids-short-wrap-zip';
      if (state.style === 'double_zip') return root.dataset.urlDoubleZip || '/collections/billy-kids-double-zip';
      if (state.style === 'straight_zip') return root.dataset.urlStraightZip || '/collections/billy-kids-straight-zip';
      return shopAll;
    };

    const renderResults = () => {
      if (!track || !empty) return;
      if (viewAll) viewAll.href = buildViewAllHref();

      if (!state.brand && !state.style && !state.size) {
        const starter = catalog.filter((p) => p.available !== false).slice(0, resultLimit);
        empty.hidden = true;
        if (trackWrap) trackWrap.hidden = false;
        track.innerHTML = starter.map(cardHtml).join('');
        return;
      }

      const matches = filterProducts();
      const show = matches.slice(0, resultLimit);

      if (!show.length) {
        track.innerHTML = '';
        if (trackWrap) trackWrap.hidden = true;
        empty.hidden = false;
        return;
      }

      empty.hidden = true;
      if (trackWrap) trackWrap.hidden = false;
      track.innerHTML = show.map(cardHtml).join('');
    };

    const refresh = () => {
      syncDesktopBodies();
      updateStyleAvailability();
      renderSizes();
      renderResults();
      setProgress();
    };

    root.querySelectorAll('[data-pb-fit-brand]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const value = btn.getAttribute('data-pb-fit-brand');
        state.brand = state.brand === value ? '' : value;
        root.querySelectorAll('[data-pb-fit-brand]').forEach((b) => {
          const on = b.getAttribute('data-pb-fit-brand') === state.brand;
          b.setAttribute('aria-pressed', on ? 'true' : 'false');
          b.classList.toggle('is-active', on);
        });
        if (state.style) {
          const still = filterProducts({ size: '' }).some((p) => (p.styles || []).includes(state.style));
          if (!still) {
            state.style = '';
            root.querySelectorAll('[data-pb-fit-style]').forEach((b) => {
              b.setAttribute('aria-pressed', 'false');
              b.classList.remove('is-active');
            });
          }
        }
        state.size = '';
        openStep(state.style ? 'size' : 'style');
        refresh();
      });
    });

    root.querySelectorAll('[data-pb-fit-style]').forEach((btn) => {
      btn.addEventListener('click', () => {
        if (btn.disabled) return;
        const value = btn.getAttribute('data-pb-fit-style');
        state.style = state.style === value ? '' : value;
        root.querySelectorAll('[data-pb-fit-style]').forEach((b) => {
          const on = b.getAttribute('data-pb-fit-style') === state.style;
          b.setAttribute('aria-pressed', on ? 'true' : 'false');
          b.classList.toggle('is-active', on);
        });
        state.size = '';
        openStep('size');
        refresh();
      });
    });

    sizesHost?.addEventListener('click', (event) => {
      const btn = event.target.closest('[data-pb-fit-size]');
      if (!btn || btn.disabled) return;
      const value = btn.getAttribute('data-pb-fit-size');
      state.size = state.size === value ? '' : value;
      openStep(state.size ? 'matches' : 'size');
      refresh();
    });

    track?.addEventListener('click', (event) => {
      const swatch = event.target.closest('[data-pb-fit-colorway]');
      if (!swatch) return;
      event.preventDefault();
      event.stopPropagation();
      const handle = swatch.getAttribute('data-pb-fit-colorway');
      const item = catalog.find((x) => x.handle === handle);
      const card = swatch.closest('[data-pb-fit-card]');
      if (!item || !card) return;
      card.querySelectorAll('[data-pb-fit-colorway]').forEach((b) => {
        const on = b === swatch;
        b.classList.toggle('is-active', on);
        b.setAttribute('aria-pressed', on ? 'true' : 'false');
      });
      const img = card.querySelector('[data-pb-fit-img]');
      if (img && item.image) {
        img.src = item.image;
        img.alt = item.title || '';
      }
      const titleEl = card.querySelector('[data-pb-fit-title]');
      if (titleEl) titleEl.textContent = item.title || '';
      const priceEl = card.querySelector('[data-pb-fit-price]');
      if (priceEl) priceEl.textContent = item.price_formatted || '';
      card.querySelectorAll('[data-pb-fit-link]').forEach((a) => {
        a.setAttribute('href', item.url || a.getAttribute('href'));
        if (a.hasAttribute('aria-label')) a.setAttribute('aria-label', item.title || '');
      });
    });

    root.querySelectorAll('[data-pb-fit-toggle]').forEach((toggle) => {
      toggle.addEventListener('click', () => {
        if (!isMobileLayout()) return;
        const step = toggle.closest('[data-pb-fit-step]');
        if (!step) return;
        openStep(step.getAttribute('data-pb-fit-step'));
      });
    });

    root.querySelectorAll('[data-pb-fit-node]').forEach((node) => {
      node.addEventListener('click', () => {
        const key = node.getAttribute('data-pb-fit-node');
        if (!key) return;
        openStep(key);
      });
    });

    window.addEventListener('resize', () => {
      syncDesktopBodies();
      if (!isMobileLayout()) {
        root.querySelectorAll('[data-pb-fit-step]').forEach((step) => {
          step.classList.add('is-active');
        });
      }
    });

    openStep('brand');
    refresh();
  });
}
window.initPbFindYourFit = initPbFindYourFit;

/**
 * Bundle & Save — hotspot sync + add all to cart
 */
function initPbBundle(scope) {
  const rootScope = scope && scope.querySelectorAll ? scope : document;
  const roots = rootScope.querySelectorAll
    ? rootScope.querySelectorAll('[data-pb-bundle]')
    : [];

  roots.forEach((root) => {
    if (root.dataset.pbBundleBound === '1') return;
    root.dataset.pbBundleBound = '1';

    const hotspots = Array.from(root.querySelectorAll('[data-pb-bundle-hotspot]'));
    const cards = Array.from(root.querySelectorAll('[data-pb-bundle-card]'));
    const addBtn = root.querySelector('[data-pb-bundle-add]');

    const setActive = (index) => {
      hotspots.forEach((h) => {
        h.classList.toggle('is-active', Number(h.dataset.index) === index);
      });
      cards.forEach((c) => {
        c.classList.toggle('is-active', Number(c.dataset.index) === index);
      });
    };

    hotspots.forEach((h) => {
      h.addEventListener('click', () => setActive(Number(h.dataset.index)));
    });
    cards.forEach((c) => {
      c.addEventListener('click', (e) => {
        if (e.target.closest('select')) return;
        setActive(Number(c.dataset.index));
      });
    });

    addBtn?.addEventListener('click', async () => {
      const items = [];
      cards.forEach((card) => {
        const select = card.querySelector('[data-pb-bundle-variant]');
        const id = Number(select?.value || 0);
        if (id) items.push({ id, quantity: 1 });
      });

      if (!items.length) {
        addBtn.classList.add('is-empty');
        window.setTimeout(() => addBtn.classList.remove('is-empty'), 600);
        return;
      }

      addBtn.disabled = true;
      try {
        if (typeof window.pebbleAddToCart === 'function') {
          await window.pebbleAddToCart(items);
        }
      } catch (err) {
        console.error(err);
      } finally {
        addBtn.disabled = false;
      }
    });
  });
}

window.initPbBundle = initPbBundle;

/**
 * Promo cards slider — drag scroll + progress + arrows
 */
function initPbPromoSlider(scope) {
  const rootScope = scope && scope.querySelectorAll ? scope : document;
  const roots = rootScope.querySelectorAll
    ? rootScope.querySelectorAll('[data-pb-promo-slider]')
    : [];

  roots.forEach((root) => {
    if (root.dataset.pbPromoBound === '1') return;
    root.dataset.pbPromoBound = '1';

    const track = root.querySelector('[data-pb-promo-track]');
    const prev = root.querySelector('[data-pb-promo-prev]');
    const next = root.querySelector('[data-pb-promo-next]');
    const progress = root.querySelector('[data-pb-promo-progress]');
    if (!track) return;

    const step = () => {
      const card = track.querySelector('.pb-promo__card');
      if (!card) return 280;
      const styles = window.getComputedStyle(track);
      const gap = parseFloat(styles.columnGap || styles.gap || '18') || 18;
      return card.getBoundingClientRect().width + gap;
    };

    const update = () => {
      const max = track.scrollWidth - track.clientWidth;
      const ratio = max > 0 ? track.scrollLeft / max : 0;
      if (progress) progress.style.transform = `scaleX(${Math.min(Math.max(ratio, 0.08), 1)})`;
      if (prev) prev.disabled = track.scrollLeft <= 4;
      if (next) next.disabled = track.scrollLeft >= max - 4;
    };

    prev?.addEventListener('click', () => {
      track.scrollBy({ left: -step(), behavior: 'smooth' });
    });
    next?.addEventListener('click', () => {
      track.scrollBy({ left: step(), behavior: 'smooth' });
    });

    let isDown = false;
    let startX = 0;
    let scrollLeft = 0;
    let moved = false;

    const onDown = (e) => {
      isDown = true;
      moved = false;
      track.classList.add('is-dragging');
      startX = (e.pageX || e.touches?.[0]?.pageX || 0) - track.offsetLeft;
      scrollLeft = track.scrollLeft;
    };
    const onUp = () => {
      isDown = false;
      track.classList.remove('is-dragging');
    };
    const onMove = (e) => {
      if (!isDown) return;
      const x = (e.pageX || e.touches?.[0]?.pageX || 0) - track.offsetLeft;
      const walk = (x - startX) * 1.15;
      if (Math.abs(walk) > 4) moved = true;
      track.scrollLeft = scrollLeft - walk;
      if (e.cancelable) e.preventDefault();
    };

    track.addEventListener('mousedown', onDown);
    window.addEventListener('mouseup', onUp);
    window.addEventListener('mousemove', onMove);
    track.addEventListener('touchstart', onDown, { passive: true });
    track.addEventListener('touchend', onUp);
    track.addEventListener('touchmove', onMove, { passive: false });
    track.addEventListener('scroll', update, { passive: true });
    track.addEventListener('click', (e) => {
      if (moved) {
        e.preventDefault();
        e.stopPropagation();
      }
    }, true);
    window.addEventListener('resize', update);

    update();
  });
}

window.initPbPromoSlider = initPbPromoSlider;

/**
 * Homepage hero slideshow — swipe, arrows, dots (FaridHub visual style)
 */
function initPbHeroSlideshow(scope) {
  const rootScope = scope && scope.querySelectorAll ? scope : document;
  const roots = rootScope.querySelectorAll
    ? rootScope.querySelectorAll('[data-pb-hero-slideshow]')
    : [];

  roots.forEach((root) => {
    if (root.dataset.pbHeroBound === '1') return;
    root.dataset.pbHeroBound = '1';

    const slides = Array.from(root.querySelectorAll('[data-pb-hero-slide]'));
    if (slides.length < 2) return;

    const dots = Array.from(root.querySelectorAll('[data-pb-hero-dot]'));
    const prev = root.querySelector('[data-pb-hero-prev]');
    const next = root.querySelector('[data-pb-hero-next]');
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const autoplay = root.getAttribute('data-autoplay') === 'true' && !reduce;
    const interval = Number(root.getAttribute('data-interval') || 6000);
    let index = 0;
    let timer = null;
    let touchX = 0;
    let touchY = 0;
    let axis = null;

    const goTo = (i) => {
      index = (i + slides.length) % slides.length;
      slides.forEach((slide, n) => {
        const on = n === index;
        slide.classList.toggle('is-active', on);
        slide.setAttribute('aria-hidden', on ? 'false' : 'true');
      });
      dots.forEach((dot, n) => {
        const on = n === index;
        dot.classList.toggle('is-active', on);
        dot.setAttribute('aria-selected', on ? 'true' : 'false');
      });
    };

    const startTimer = () => {
      stopTimer();
      if (!autoplay) return;
      timer = window.setInterval(() => goTo(index + 1), interval);
    };
    const stopTimer = () => {
      if (timer) window.clearInterval(timer);
      timer = null;
    };

    prev?.addEventListener('click', () => {
      goTo(index - 1);
      startTimer();
    });
    next?.addEventListener('click', () => {
      goTo(index + 1);
      startTimer();
    });
    dots.forEach((dot) => {
      dot.addEventListener('click', () => {
        goTo(Number(dot.getAttribute('data-index') || 0));
        startTimer();
      });
    });

    const track = root.querySelector('[data-pb-hero-track]') || root;
    track.addEventListener(
      'touchstart',
      (e) => {
        const t = e.touches[0];
        touchX = t.clientX;
        touchY = t.clientY;
        axis = null;
        stopTimer();
      },
      { passive: true }
    );
    track.addEventListener(
      'touchmove',
      (e) => {
        if (touchX == null) return;
        const t = e.touches[0];
        const dx = t.clientX - touchX;
        const dy = t.clientY - touchY;
        if (!axis) {
          if (Math.abs(dx) < 8 && Math.abs(dy) < 8) return;
          axis = Math.abs(dx) > Math.abs(dy) ? 'x' : 'y';
        }
        if (axis === 'x' && e.cancelable) e.preventDefault();
      },
      { passive: false }
    );
    track.addEventListener(
      'touchend',
      (e) => {
        const t = e.changedTouches[0];
        const dx = t.clientX - touchX;
        if (axis === 'x' && Math.abs(dx) > 40) goTo(index + (dx < 0 ? 1 : -1));
        touchX = null;
        axis = null;
        startTimer();
      },
      { passive: true }
    );

    root.addEventListener('mouseenter', stopTimer);
    root.addEventListener('mouseleave', startTimer);
    goTo(0);
    startTimer();
  });
}
window.initPbHeroSlideshow = initPbHeroSlideshow;

/**
 * Size guide US / EU tabs
 */
function initPbSizeGuide(scope) {
  const rootScope = scope && scope.querySelectorAll ? scope : document;
  const roots = rootScope.querySelectorAll
    ? rootScope.querySelectorAll('[data-pb-size-guide]')
    : [];

  roots.forEach((root) => {
    if (root.dataset.pbSgBound === '1') return;
    root.dataset.pbSgBound = '1';

    const tabs = Array.from(root.querySelectorAll('[data-pb-sg-tab]'));
    const panels = Array.from(root.querySelectorAll('[data-pb-sg-panel]'));
    tabs.forEach((tab) => {
      tab.addEventListener('click', () => {
        const id = tab.getAttribute('data-pb-sg-tab');
        tabs.forEach((t) => {
          const on = t === tab;
          t.classList.toggle('is-active', on);
          t.setAttribute('aria-selected', on ? 'true' : 'false');
        });
        panels.forEach((panel) => {
          const on = panel.getAttribute('data-pb-sg-panel') === id;
          panel.classList.toggle('is-active', on);
          panel.hidden = !on;
        });
      });
    });
  });
}
window.initPbSizeGuide = initPbSizeGuide;

/**
 * Cinematic full-bleed hero
 */
function initPbCineHero(scope) {
  const rootScope = scope && scope.querySelectorAll ? scope : document;
  const roots = rootScope.querySelectorAll
    ? rootScope.querySelectorAll('[data-pb-cine-hero]')
    : [];

  roots.forEach((root) => {
    if (root.dataset.pbCineBound === '1') return;
    root.dataset.pbCineBound = '1';

    const slides = Array.from(root.querySelectorAll('[data-pb-cine-slide]'));
    if (slides.length < 2) return;

    const dots = Array.from(root.querySelectorAll('[data-pb-cine-dot]'));
    const prev = root.querySelector('[data-pb-cine-prev]');
    const next = root.querySelector('[data-pb-cine-next]');
    const progress = root.querySelector('[data-pb-cine-progress]');
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const autoplay = root.getAttribute('data-autoplay') === 'true' && !reduce;
    const interval = Number(root.getAttribute('data-interval') || 7000);
    let index = 0;
    let timer = null;
    let started = 0;
    let touchX = 0;
    let axis = null;

    const goTo = (i) => {
      index = (i + slides.length) % slides.length;
      slides.forEach((slide, n) => {
        const on = n === index;
        slide.classList.toggle('is-active', on);
        slide.setAttribute('aria-hidden', on ? 'false' : 'true');
      });
      dots.forEach((dot, n) => {
        const on = n === index;
        dot.classList.toggle('is-active', on);
        dot.setAttribute('aria-selected', on ? 'true' : 'false');
      });
      if (progress) {
        progress.style.transition = 'none';
        progress.style.width = '0%';
        // force reflow
        void progress.offsetWidth;
        if (autoplay) {
          progress.style.transition = `width ${interval}ms linear`;
          progress.style.width = '100%';
        }
      }
    };

    const stop = () => {
      if (timer) window.clearTimeout(timer);
      timer = null;
    };
    const start = () => {
      stop();
      if (!autoplay) return;
      started = Date.now();
      if (progress) {
        progress.style.transition = `width ${interval}ms linear`;
        progress.style.width = '100%';
      }
      timer = window.setTimeout(() => {
        goTo(index + 1);
        start();
      }, interval);
    };

    prev?.addEventListener('click', () => { goTo(index - 1); start(); });
    next?.addEventListener('click', () => { goTo(index + 1); start(); });
    dots.forEach((dot) => {
      dot.addEventListener('click', () => {
        goTo(Number(dot.getAttribute('data-index') || 0));
        start();
      });
    });

    root.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowLeft') { goTo(index - 1); start(); }
      if (e.key === 'ArrowRight') { goTo(index + 1); start(); }
    });
    root.setAttribute('tabindex', '0');

    root.addEventListener('mouseenter', stop);
    root.addEventListener('mouseleave', start);
    root.addEventListener('focusin', stop);
    root.addEventListener('focusout', start);

    root.addEventListener('touchstart', (e) => {
      touchX = e.touches[0].clientX;
      axis = null;
      stop();
    }, { passive: true });
    root.addEventListener('touchmove', (e) => {
      const dx = e.touches[0].clientX - touchX;
      const dy = e.touches[0].clientY - (e.touches[0].clientY);
      if (!axis && Math.abs(dx) > 8) axis = 'x';
      if (axis === 'x' && e.cancelable) e.preventDefault();
    }, { passive: false });
    root.addEventListener('touchend', (e) => {
      const dx = e.changedTouches[0].clientX - touchX;
      if (axis === 'x' && Math.abs(dx) > 40) goTo(index + (dx < 0 ? 1 : -1));
      start();
    }, { passive: true });

    goTo(0);
    start();
  });
}
window.initPbCineHero = initPbCineHero;

document.addEventListener('DOMContentLoaded', () => {
  initPebbleReveal();
  initPbScrollingCards();
  initPbLayeredCards();
  initPbHighlightSlider();
  initPbProductsHighlight();
  initPbFlipTop();
  initPbStyleIt();
  initPbFindYourFit();
  initPbProductCards();
  initPbBundle();
  initPbPromoSlider();
  initPbHeroSlideshow();
  initPbCineHero();
  initPbSizeGuide();
  initPbPdp();
  initPbCollection();
  initPbStores();
});
document.addEventListener('shopify:section:load', (event) => {
  initPebbleReveal();
  initPbScrollingCards(event.target);
  initPbLayeredCards(event.target);
  initPbHighlightSlider(event.target);
  initPbProductsHighlight(event.target);
  initPbFlipTop(event.target);
  initPbStyleIt(event.target);
  initPbFindYourFit(event.target);
  initPbProductCards(event.target);
  initPbBundle(event.target);
  initPbPromoSlider(event.target);
  initPbHeroSlideshow(event.target);
  initPbCineHero(event.target);
  initPbSizeGuide(event.target);
  initPbPdp(event.target);
  initPbCollection(event.target);
  initPbStores(event.target);
});
if (document.readyState !== 'loading') {
  initPebbleReveal();
  initPbScrollingCards();
  initPbLayeredCards();
  initPbHighlightSlider();
  initPbProductsHighlight();
  initPbFlipTop();
  initPbStyleIt();
  initPbFindYourFit();
  initPbProductCards();
  initPbBundle();
  initPbPromoSlider();
  initPbHeroSlideshow();
  initPbCineHero();
  initPbSizeGuide();
  initPbPdp();
  initPbCollection();
  initPbStores();
}

/**
 * FaridHub PDP — delegated to assets/pebble-pdp.js
 */
function initPbPdp(scope) {
  if (typeof window.initFhPdp === 'function') {
    window.initFhPdp(scope);
    return;
  }
  const rootScope = scope && scope.querySelectorAll ? scope : document;
  const roots = rootScope.querySelectorAll
    ? rootScope.querySelectorAll('[data-pb-pdp]')
    : [];

  roots.forEach((root) => {
    if (root.dataset.pbPdpBound === '1') return;
    root.dataset.pbPdpBound = '1';

    const jsonEl = root.querySelector('[data-pb-pdp-json]');
    let product = null;
    try {
      product = jsonEl ? JSON.parse(jsonEl.textContent) : null;
    } catch (e) {
      product = null;
    }

    const thumbs = root.querySelectorAll('[data-pb-pdp-thumb]');
    const medias = Array.from(root.querySelectorAll('[data-pb-pdp-media]'));
    const dots = root.querySelectorAll('[data-pb-pdp-dot]');
    const slider = root.querySelector('[data-pb-pdp-slider]');

    const goToSlide = (idx, { smooth = true } = {}) => {
      const index = Number(idx);
      const target = medias.find((m) => Number(m.getAttribute('data-index')) === index);
      thumbs.forEach((t) => t.classList.toggle('is-active', Number(t.getAttribute('data-index')) === index));
      dots.forEach((d) => d.classList.toggle('is-active', Number(d.getAttribute('data-index')) === index));
      medias.forEach((m) => m.classList.toggle('is-active', Number(m.getAttribute('data-index')) === index));
      if (!target || !slider) return;
      const isMobileSlider = window.matchMedia('(max-width: 989px)').matches;
      if (isMobileSlider) {
        slider.scrollTo({ left: target.offsetLeft, behavior: smooth ? 'smooth' : 'auto' });
      } else if (smooth) {
        target.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }
    };

    thumbs.forEach((thumb) => {
      thumb.addEventListener('click', () => goToSlide(thumb.getAttribute('data-index')));
    });
    dots.forEach((dot) => {
      dot.addEventListener('click', () => goToSlide(dot.getAttribute('data-index')));
    });

    if (slider && medias.length > 1) {
      let scrollTick = 0;
      slider.addEventListener(
        'scroll',
        () => {
          if (scrollTick) return;
          scrollTick = window.requestAnimationFrame(() => {
            scrollTick = 0;
            if (!window.matchMedia('(max-width: 989px)').matches) return;
            const center = slider.scrollLeft + slider.clientWidth / 2;
            let closest = medias[0];
            let best = Infinity;
            medias.forEach((m) => {
              const mid = m.offsetLeft + m.clientWidth / 2;
              const dist = Math.abs(mid - center);
              if (dist < best) {
                best = dist;
                closest = m;
              }
            });
            const idx = Number(closest.getAttribute('data-index'));
            thumbs.forEach((t) => t.classList.toggle('is-active', Number(t.getAttribute('data-index')) === idx));
            dots.forEach((d) => d.classList.toggle('is-active', Number(d.getAttribute('data-index')) === idx));
            medias.forEach((m) => m.classList.toggle('is-active', Number(m.getAttribute('data-index')) === idx));
          });
        },
        { passive: true }
      );
    }

    if (!product || !Array.isArray(product.variants)) return;

    const optionNodes = Array.from(root.querySelectorAll('[data-pb-pdp-option]'));
    const selected = product.options.map((optName, index) => {
      const node = optionNodes.find((n) => Number(n.getAttribute('data-option-index')) === index);
      const selectedBtn = node?.querySelector('[data-pb-pdp-value].is-selected');
      return selectedBtn?.getAttribute('data-value') || product.variants[0]?.options?.[index] || '';
    });

    const money = (cents) => {
      const chosen = window.theme?.moneyFormat || window.Shopify?.money_format;
      try {
        if (window.Shopify?.formatMoney) {
          return window.Shopify.formatMoney(cents, chosen);
        }
      } catch (e) {}
      const value = (Number(cents) || 0) / 100;
      const symbol = window.theme?.currencySymbol || window.theme?.currency || '';
      return `${symbol} ${value.toFixed(2)}`.trim();
    };

    const findVariant = () =>
      product.variants.find((v) =>
        (v.options || []).every((opt, i) => String(opt) === String(selected[i]))
      );

    const syncUI = () => {
      const variant = findVariant() || product.variants.find((v) => v.available) || product.variants[0];
      if (!variant) return;

      // Keep selected options in sync with resolved variant when combo missing
      if (Array.isArray(variant.options)) {
        variant.options.forEach((opt, i) => {
          selected[i] = String(opt);
        });
      }

      const idInput = root.querySelector('[data-pb-pdp-variant-id]');
      if (idInput) idInput.value = variant.id;

      const priceEl = root.querySelector('[data-pb-pdp-price]');
      if (priceEl) {
        if (variant.compare_at_price > variant.price) {
          priceEl.innerHTML = `<span class="pb-pdp__price-now">${money(variant.price)}</span><s class="pb-pdp__price-was">${money(variant.compare_at_price)}</s>`;
        } else {
          priceEl.innerHTML = `<span class="pb-pdp__price-now pb-pdp__price-now--regular">${money(variant.price)}</span>`;
        }
      }

      const atc = root.querySelector('[data-pb-pdp-atc]');
      if (atc) {
        const isPreorder = atc.dataset.preorder === 'true';
        const baseLabel = atc.dataset.label || (isPreorder ? 'Preorder' : 'Add To Cart');
        atc.disabled = !variant.available;
        atc.textContent = variant.available ? baseLabel : 'Sold out';
      }

      const stock = root.querySelector('[data-pb-pdp-stock]');
      const stockQty = root.querySelector('[data-pb-pdp-stock-qty]');
      if (stock && stockQty) {
        const qty = Number(variant.inventory_quantity || 0);
        stockQty.textContent = String(qty);
        stock.hidden = !(variant.available && qty > 0 && qty <= 8);
      }

      // Featured media for selected variant
      if (variant.featured_media?.id != null && medias.length) {
        const mediaIndex = medias.findIndex((m) => {
          const img = m.querySelector('img');
          return img && String(variant.featured_image?.id || '') && img.src.includes(String(variant.featured_image?.id));
        });
        // Prefer matching by media position from product.media
        const mediaList = product.media || [];
        const idxFromProduct = mediaList.findIndex((m) => m.id === variant.featured_media.id);
        if (idxFromProduct >= 0) goToSlide(idxFromProduct, { smooth: false });
      }

      optionNodes.forEach((node, index) => {
        const label = node.querySelector('[data-pb-pdp-option-value]');
        if (label) label.textContent = selected[index] || '';

        node.querySelectorAll('[data-pb-pdp-value]').forEach((btn) => {
          const value = btn.getAttribute('data-value') || '';
          const on = String(selected[index]) === String(value);
          btn.classList.toggle('is-selected', on);
          btn.setAttribute('aria-pressed', on ? 'true' : 'false');

          // Disable visually when no available variant exists for this value + other selections
          const trial = selected.slice();
          trial[index] = value;
          const hasAvailable = product.variants.some(
            (v) =>
              v.available &&
              (v.options || []).every((opt, i) => String(opt) === String(trial[i]))
          );
          const exists = product.variants.some((v) =>
            (v.options || []).every((opt, i) => String(opt) === String(trial[i]))
          );
          const unavailable = !exists || !hasAvailable;
          btn.classList.toggle('is-unavailable', unavailable);
          if (unavailable) btn.setAttribute('aria-disabled', 'true');
          else btn.removeAttribute('aria-disabled');
        });
      });
    };

    optionNodes.forEach((node) => {
      const index = Number(node.getAttribute('data-option-index'));
      node.querySelectorAll('[data-pb-pdp-value]').forEach((btn) => {
        btn.addEventListener('click', () => {
          selected[index] = btn.getAttribute('data-value') || '';
          syncUI();
        });
      });
    });

    const atcBtn = root.querySelector('[data-pb-pdp-atc]');
    if (atcBtn && !atcBtn.dataset.label) atcBtn.dataset.label = atcBtn.textContent.trim();

    const form = root.querySelector('[data-pb-pdp-form]');
    if (form) {
      form.addEventListener('submit', async (event) => {
        event.preventDefault();
        const variant = findVariant();
        if (!variant?.available) return;
        const qty = Number(qtyInput?.value || 1);
        const payload = { id: variant.id, quantity: qty };
        if (form.getAttribute('data-pb-preorder') === 'true') {
          payload.properties = { _Preorder: 'Yes' };
        }
        try {
          await window.pebbleAddToCart(payload);
        } catch (e) {
          console.error(e);
        }
      });
    }

    syncUI();
  });
}
/**
 * FaridHub collection — filters, sort, mobile drawer
 */
function initPbCollection(scope) {
  const rootScope = scope && scope.querySelectorAll ? scope : document;
  const roots = rootScope.querySelectorAll
    ? rootScope.querySelectorAll('.pb-coll')
    : [];

  roots.forEach((root) => {
    if (root.dataset.pbCollBound === '1') return;
    root.dataset.pbCollBound = '1';

    const form = root.querySelector('[data-pb-coll-filters]');
    const sort = root.querySelector('[data-pb-coll-sort]');
    const sortHidden = root.querySelector('[data-pb-coll-sort-hidden]');
    const toggle = root.querySelector('[data-pb-coll-filter-toggle]');
    const sidebar = root.querySelector('[data-pb-coll-sidebar]');

    const submitFilters = () => {
      if (!form) return;
      form.submit();
    };

    if (form) {
      form.querySelectorAll('[data-pb-filter-more]').forEach((button) => {
        button.addEventListener('click', () => {
          const expanded = button.getAttribute('aria-expanded') === 'true';
          const list = button.previousElementSibling;
          if (!list) return;
          list.querySelectorAll('.pb-coll__filter-extra').forEach((item) => {
            item.hidden = expanded;
          });
          button.setAttribute('aria-expanded', expanded ? 'false' : 'true');
          button.textContent = expanded ? button.dataset.moreLabel : button.dataset.lessLabel;
        });
      });

      form.addEventListener('change', (event) => {
        const target = event.target;
        if (!target) return;
        if (target.matches('[data-pb-sizing]')) {
          const group = target.getAttribute('data-pb-sizing');
          const checked = target.checked;
          form.querySelectorAll('[data-pb-gender-group]').forEach((input) => {
            if (input.getAttribute('data-pb-gender-group') === group) {
              input.checked = checked;
            }
          });
        }
        if (target.matches('input[type="checkbox"]')) submitFilters();
      });
    }

    if (sort) {
      sort.addEventListener('change', () => {
        if (sortHidden) sortHidden.value = sort.value;
        if (form) {
          submitFilters();
          return;
        }
        const url = new URL(window.location.href);
        url.searchParams.set('sort_by', sort.value);
        window.location.href = url.toString();
      });
    }

    if (toggle && sidebar) {
      const setOpen = (open) => {
        root.classList.toggle('is-filters-open', open);
        toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
        document.documentElement.style.overflow = open ? 'hidden' : '';
      };

      toggle.addEventListener('click', () => {
        setOpen(!root.classList.contains('is-filters-open'));
      });

      sidebar.addEventListener('click', (event) => {
        if (event.target === sidebar) setOpen(false);
      });

      document.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') setOpen(false);
      });
    }
  });
}

window.initPbCollection = initPbCollection;

/**
 * FaridHub Find A Store carousel
 */
function initPbStores(scope) {
  const rootScope = scope && scope.querySelectorAll ? scope : document;
  const roots = rootScope.querySelectorAll
    ? rootScope.querySelectorAll('[data-pb-stores]')
    : [];

  roots.forEach((root) => {
    if (root.dataset.pbStoresBound === '1') return;
    root.dataset.pbStoresBound = '1';

    const track = root.querySelector('[data-pb-stores-track]');
    const progress = root.querySelector('[data-pb-stores-progress]');
    const prev = root.querySelector('[data-pb-stores-prev]');
    const next = root.querySelector('[data-pb-stores-next]');
    if (!track) return;

    const updateProgress = () => {
      if (!progress) return;
      const max = track.scrollWidth - track.clientWidth;
      const ratio = max > 0 ? track.scrollLeft / max : 0;
      const width = Math.max(18, Math.min(100, 18 + ratio * 82));
      progress.style.width = `${width}%`;
    };

    const scrollByCard = (dir) => {
      const card = track.querySelector('.pb-stores__card');
      const amount = card ? card.getBoundingClientRect().width + 16 : track.clientWidth * 0.8;
      track.scrollBy({ left: dir * amount, behavior: 'smooth' });
    };

    track.addEventListener('scroll', updateProgress, { passive: true });
    if (prev) prev.addEventListener('click', () => scrollByCard(-1));
    if (next) next.addEventListener('click', () => scrollByCard(1));
    updateProgress();
  });
}

window.initPbStores = initPbStores;

/**
 * Keep mobile fixed-header offset in sync (announce + header bar only).
 * Prevents content sliding under the chrome and avoids iOS “floating gap” layout.
 */
function updatePbHeaderOffset() {
  const group = document.querySelector('#header-group');
  if (!group || !group.querySelector('.pb-header')) return;

  const announce = group.querySelector('.pb-announce');
  const headerFloat = group.querySelector('.pb-header-float');
  let height = 0;
  if (announce) height += announce.getBoundingClientRect().height;
  if (headerFloat) height += headerFloat.getBoundingClientRect().height;
  if (!height) {
    // Fallback: section height without teleported/fixed chrome
    const section = group.querySelector('.pb-header-section') || group;
    height = section.getBoundingClientRect().height;
  }

  const rounded = Math.ceil(height);
  if (rounded > 0) {
    document.documentElement.style.setProperty('--pb-header-offset', `${rounded}px`);
    document.body.style.setProperty('--header-group-height', `${rounded}px`);
    document.body.style.setProperty('--header-height', `${rounded}px`);
  }
}

function relocatePbBottomNav() {
  const nav = document.querySelector('[data-pb-bottom-nav]');
  if (!nav || nav.dataset.pbRelocated === '1') return;
  document.body.appendChild(nav);
  nav.dataset.pbRelocated = '1';
}

function initPbHeaderOffset() {
  relocatePbBottomNav();
  updatePbHeaderOffset();

  window.addEventListener('resize', updatePbHeaderOffset, { passive: true });
  window.addEventListener('orientationchange', updatePbHeaderOffset, { passive: true });

  if (typeof ResizeObserver !== 'undefined') {
    const group = document.querySelector('#header-group');
    if (group) {
      const ro = new ResizeObserver(() => updatePbHeaderOffset());
      const announce = group.querySelector('.pb-announce');
      const headerFloat = group.querySelector('.pb-header-float');
      if (announce) ro.observe(announce);
      if (headerFloat) ro.observe(headerFloat);
    }
  }

  document.addEventListener('shopify:section:load', () => {
    relocatePbBottomNav();
    updatePbHeaderOffset();
  });
  document.addEventListener('shopify:section:reorder', updatePbHeaderOffset);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initPbHeaderOffset, { once: true });
} else {
  initPbHeaderOffset();
}
window.updatePbHeaderOffset = updatePbHeaderOffset;
